import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as loginApi, getMe } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await getMe();
          setUser(response.data.user);
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (credentials) => {
    const response = await loginApi(credentials);
    const { token, user } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (newUserData) => {
    setUser(prev => ({ ...prev, ...newUserData }));
    localStorage.setItem('user', JSON.stringify({ ...user, ...newUserData }));
  };

  // Permission helpers
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return user.permissions?.[permission] || false;
  };

  const canViewVendors = hasPermission('vendors_view');
  const canEditVendors = hasPermission('vendors_edit');
  const canViewInvoices = hasPermission('invoices_view');
  const canEditInvoices = hasPermission('invoices_edit');
  const canApproveInvoices = hasPermission('invoices_approve');
  const canDeleteInvoices = hasPermission('invoices_delete');
  const canViewProjects = hasPermission('projects_view');
  const canEditProjects = hasPermission('projects_edit');
  const canViewCompanies = hasPermission('companies_view');
  const canEditCompanies = hasPermission('companies_edit');
  const canViewEmployees = hasPermission('employees_view');
  const canEditEmployees = hasPermission('employees_edit');
  const canManageUsers = hasPermission('users_manage');
  const canViewCustomers = hasPermission('customers_view');
  const canEditCustomers = hasPermission('customers_edit');
  const canViewARInvoices = hasPermission('ar_invoices_view');
  const canEditARInvoices = hasPermission('ar_invoices_edit');
  const canViewChannels = hasPermission('channels_view');
  const canEditChannels = hasPermission('channels_edit');

  const isManager = user?.role === 'manager' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const hasDirectReports = (user?.direct_reports_count || 0) > 0;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateUser,
      hasPermission,
      canViewVendors,
      canEditVendors,
      canViewInvoices,
      canEditInvoices,
      canApproveInvoices,
      canDeleteInvoices,
      canViewProjects,
      canEditProjects,
      canViewCompanies,
      canEditCompanies,
      canViewEmployees,
      canEditEmployees,
      canManageUsers,
      canViewCustomers,
      canEditCustomers,
      canViewARInvoices,
      canEditARInvoices,
      canViewChannels,
      canEditChannels,
      isManager,
      isAdmin,
      isHR,
      hasDirectReports
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
